import { subscribeToCartEvents, type CartEvent } from "@ecommerce/cart";
import { createMetricService } from "@ecommerce/http-services";
import type { HttpClient } from "@ecommerce/http-services";
import type { MetricCode, MetricEntityRef } from "./metrics.types";

let service: ReturnType<typeof createMetricService> | null = null;
let enabledCodes = new Set<string>();
let enabledPromise: Promise<Set<string>> = Promise.resolve(enabledCodes);
let refreshPromise: Promise<Set<string>> | null = null;
let lastRefreshStartedAt = 0;
const registeredViews = new Set<string>();
const CART_METRIC_SUBSCRIBER_ID = "@ecommerce/analytics:metrics";
let unsubscribeCartEvents: (() => void) | null = null;

function handleCartEvent(event: CartEvent): void {
    if (event.type !== "ITEM_ADDED" || !event.item.idProduct) return;

    trackMetric("PRODUCT_ADD_TO_CART", {
        entityId: String(event.item.idProduct),
    });
}

export function initMetricTracker(httpClient: HttpClient): Promise<void> {
    service = createMetricService(httpClient);
    unsubscribeCartEvents?.();
    unsubscribeCartEvents = subscribeToCartEvents(CART_METRIC_SUBSCRIBER_ID, handleCartEvent);
    return refreshEnabledMetrics(true);
}

export function refreshEnabledMetrics(force = false): Promise<void> {
    if (!service) return Promise.resolve();
    if (refreshPromise) return refreshPromise.then(() => undefined);

    const now = Date.now();
    if (!force && now - lastRefreshStartedAt < 1000) return enabledPromise.then(() => undefined);

    lastRefreshStartedAt = now;
    refreshPromise = service
        .getEnabledMetrics()
        .then(({ codes }) => {
            enabledCodes = new Set(codes);
            return enabledCodes;
        })
        .catch(() => enabledCodes)
        .finally(() => {
            refreshPromise = null;
        });
    enabledPromise = refreshPromise;

    return enabledPromise.then(() => undefined);
}

export function trackMetric(code: MetricCode, entity: MetricEntityRef): void {
    if (code === "PRODUCT_ORDERED") return;

    void (async () => {
        const enabled = await enabledPromise;
        if (!service || !enabled.has(code)) return;
        await service.recordEvent({ metricCode: code, ...entity });
    })().catch(() => undefined);
}

export function trackMetricViewOnce(
    code: "PRODUCT_VIEW" | "CATEGORY_VIEW",
    entity: MetricEntityRef,
    navigationKey: string,
): void {
    const entityKey = "entityId" in entity ? entity.entityId : entity.entitySku;
    const key = `${code}:${entityKey}:${navigationKey}`;
    if (registeredViews.has(key)) return;

    registeredViews.add(key);
    trackMetric(code, entity);
}
